import React, { useEffect, useMemo, useRef, useState } from "react";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Crosshair, Locate, Navigation2, AlertTriangle, Loader2, Phone, Activity, MapPin } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useGeolocation } from "../hooks/useGeolocation";
import { getStyle, applySatelliteZoomCap } from "../lib/mapStyles";
import { addRouteLayer, requestRoute, renderRouteOnMap, clearRoute } from "../lib/mapTraffic";
import { useAgencyAuth } from "../lib/useIdentity";
import { agencyAuth } from "../lib/identity";
import { RouteDrawer } from "../components/RouteDrawer";
import { useCountryConfig } from "../lib/useCountryConfig";
import { useAgencyFirehose } from "../lib/useAgencyFirehose";
import { classifyLocationQuality, fmtLocationFreshness, QUALITY_LABELS_FR } from "../components/heatmap/heatmapHelpers";

/**
 * Iter67 — Agency-scoped SOS Mali map view at /agence/carte?alert_id=…
 *
 * The sovereign replacement for Google Maps as the PRIMARY incident
 * navigation surface for assigned agencies. Renders:
 *
 *   • a MapLibre canvas with the same hybrid/streets/satellite style
 *     stack the public /carte page uses,
 *   • the incident pin (red) with a popup of emergency type +
 *     operational status + assigned agency,
 *   • the operator's current GPS (blue dot with pulsing halo, when
 *     permission granted),
 *   • a TomTom turn-by-turn route line from operator → incident
 *     drawn on the map + a slide-up drawer with distance, ETA, and
 *     the step-by-step instruction list,
 *   • recenter buttons (incident / current position),
 *   • a link back to the case file.
 *
 * Permissions: the underlying GET /api/agency/alerts/{id}/map endpoint
 * is gated by `_check_agency_assigned`. On HTTP 403 we show the
 * full-page "Accès refusé" panel; on 404 we show "Alerte introuvable".
 */
export default function AgencyMapPage() {
  const navigate = useNavigate();
  const [session] = useAgencyAuth();
  const [params] = useSearchParams();
  const alertId = params.get("alert_id");
  const cfg = useCountryConfig();
  const brand = cfg?.brand_name || "SOS Mali";

  const { coords: gpsCoords, error: _gpsError } = useGeolocation();
  const [alert, setAlert] = useState(null);
  const [loadErr, setLoadErr] = useState(null);
  const [loading, setLoading] = useState(true);
  // Iter90 — GPS audit: post-SOS location refinement for THIS alert,
  // pushed live by the agency firehose. Kept separate from `alert` so
  // it never re-triggers the map-mount effect (which depends on
  // `[alert]` and would tear down/rebuild the whole canvas + route).
  const [liveLocation, setLiveLocation] = useState(null);

  // Route state
  const [routeOpen, setRouteOpen] = useState(false);
  const [routeLoading, setRouteLoading] = useState(false);
  const [routeData, setRouteData] = useState(null);
  const [routeAttempted, setRouteAttempted] = useState(false);

  const mapRef = useRef(null);
  const containerRef = useRef(null);
  const incidentMarkerRef = useRef(null);
  const userMarkerRef = useRef(null);
  const flewOnceRef = useRef(false);

  // Fetch the assignment-gated alert payload.
  useEffect(() => {
    if (!alertId || !session) return;
    let cancelled = false;
    setLoading(true);
    setLoadErr(null);
    api.get(`/agency/alerts/${alertId}/map`)
      .then((r) => { if (!cancelled) setAlert(r.data); })
      .catch((e) => {
        if (cancelled) return;
        const st = e?.response?.status;
        if (st === 403) setLoadErr({ kind: "denied",   msg: e.response.data?.detail || "Accès refusé — cette alerte n'est pas assignée à votre Agence." });
        else if (st === 404) setLoadErr({ kind: "notfound", msg: "Alerte introuvable" });
        else setLoadErr({ kind: "generic", msg: e.response?.data?.detail || "Impossible de charger la carte" });
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [alertId, session]);

  // Live location refinements for this specific incident, if any land
  // while the dispatcher has this exact map open.
  useAgencyFirehose({
    enabled: !!session && !!alertId,
    onLocationUpdated: (id, msg) => { if (id === alertId) setLiveLocation(msg); },
  });

  // Effective incident coordinates — the live-refined fix once one has
  // arrived, otherwise whatever the initial /map fetch returned.
  const effectiveCoords = liveLocation?.coordinates
    ? { ...alert?.coordinates, ...liveLocation.coordinates }
    : alert?.coordinates;

  // Move the EXISTING incident marker + refresh its popup in place on
  // a live refinement — no map/route teardown.
  useEffect(() => {
    const map = mapRef.current;
    const marker = incidentMarkerRef.current;
    if (!map || !marker || !liveLocation?.coordinates) return;
    const { latitude: lat, longitude: lng, accuracy } = liveLocation.coordinates;
    marker.setLngLat([lng, lat]);
    const quality = liveLocation.location_quality || classifyLocationQuality(accuracy);
    const fresh = fmtLocationFreshness(liveLocation.location_updated_at);
    const popup = marker.getPopup();
    if (popup) {
      popup.setHTML(
        `<div style='font-family:system-ui;font-size:12px;line-height:1.4;padding:4px 2px;'>
          <div style='font-weight:700;letter-spacing:.05em;text-transform:uppercase;color:#E53935;font-size:11px'>${alert?.emergency_type || "Incident"}</div>
          <div style='color:#525252;margin-top:2px'>${alert?.operational_status || "Reçue par l'Agence"}</div>
          <div style='color:#059669;margin-top:4px;font-weight:600'>Position affinée · ±${Math.round(accuracy || 0)} m (${QUALITY_LABELS_FR[quality] || quality}) · ${fresh.label}</div>
        </div>`
      );
    }
  }, [liveLocation, alert]);

  // Mount the map once we know the incident coords (delaying mount
  // avoids the empty-Bamako-fit then snap-to-incident flash).
  useEffect(() => {
    if (!alert || !containerRef.current || mapRef.current) return undefined;
    const { latitude: lat, longitude: lng } = alert.coordinates || {};
    if (!lat && !lng) return undefined;

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: getStyle("hybrid"),
      center: [lng, lat],
      zoom: 14,
      pitch: 0,
      bearing: 0,
      attributionControl: false,
    });
    applySatelliteZoomCap(map, "hybrid");
    // Iter78 — Parity with Users Map: tilt/compass moves to bottom-left.
    map.addControl(new maplibregl.NavigationControl({ visualizePitch: true }), "bottom-left");
    map.addControl(new maplibregl.AttributionControl({ compact: true }), "bottom-left");
    mapRef.current = map;
    map.on("load", () => {
      addRouteLayer(map);
      // Incident marker — red pin, popup with key context.
      const el = document.createElement("div");
      el.className = "agency-map-incident-pin";
      el.style.cssText = "width:34px;height:34px;border-radius:50%;background:#E53935;border:4px solid white;box-shadow:0 4px 16px rgba(229,57,53,0.6);cursor:pointer;";
      const popup = new maplibregl.Popup({ offset: 18, closeButton: false }).setHTML(
        `<div style='font-family:system-ui;font-size:12px;line-height:1.4;padding:4px 2px;'>
          <div style='font-weight:700;letter-spacing:.05em;text-transform:uppercase;color:#E53935;font-size:11px'>${alert.emergency_type || "Incident"}</div>
          <div style='color:#525252;margin-top:2px'>${alert.operational_status || "Reçue par l'Agence"}</div>
        </div>`
      );
      incidentMarkerRef.current = new maplibregl.Marker({ element: el })
        .setLngLat([lng, lat])
        .setPopup(popup)
        .addTo(map);
    });
    return () => {
      map.remove();
      mapRef.current = null;
      incidentMarkerRef.current = null;
    };
  }, [alert]);

  // Add / update the operator's GPS marker.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !gpsCoords) return;
    const { latitude, longitude } = gpsCoords;
    if (!userMarkerRef.current) {
      const el = document.createElement("div");
      el.style.cssText = "width:18px;height:18px;border-radius:50%;background:#2563EB;border:3px solid white;box-shadow:0 0 0 6px rgba(37,99,235,0.25),0 4px 10px rgba(37,99,235,0.4);";
      userMarkerRef.current = new maplibregl.Marker({ element: el })
        .setLngLat([longitude, latitude])
        .addTo(map);
    } else {
      userMarkerRef.current.setLngLat([longitude, latitude]);
    }
  }, [gpsCoords]);

  // Auto-request a route from current GPS to incident, ONCE per page.
  useEffect(() => {
    if (!mapRef.current || !alert || !gpsCoords || routeAttempted) return;
    setRouteAttempted(true);
    setRouteOpen(true);
    setRouteLoading(true);
    requestRoute({
      originLat: gpsCoords.latitude,
      originLng: gpsCoords.longitude,
      destLat: alert.coordinates.latitude,
      destLng: alert.coordinates.longitude,
    })
      .then((resp) => {
        setRouteData(resp);
        const map = mapRef.current;
        if (map) renderRouteOnMap(map, resp);
      })
      .catch((e) => {
        console.warn("route request failed:", e);
        toast.error("Itinéraire indisponible — la carte affiche l'incident.");
      })
      .finally(() => setRouteLoading(false));
  }, [alert, gpsCoords, routeAttempted]);

  // Recenter on incident OR on operator position.
  const recenterOnIncident = () => {
    const map = mapRef.current;
    if (!map || !effectiveCoords) return;
    map.flyTo({ center: [effectiveCoords.longitude, effectiveCoords.latitude], zoom: 16, duration: 700 });
  };
  const recenterOnMe = () => {
    const map = mapRef.current;
    if (!map || !gpsCoords) { toast.info("Position actuelle indisponible"); return; }
    map.flyTo({ center: [gpsCoords.longitude, gpsCoords.latitude], zoom: 16, duration: 700 });
  };

  // Initial fly-once if we have both points.
  useEffect(() => {
    if (flewOnceRef.current) return;
    if (!routeData || !mapRef.current) return;
    flewOnceRef.current = true;
    // renderRouteOnMap already fit bounds; nothing to do here.
  }, [routeData]);

  const externalMapsUrl = useMemo(() => {
    if (!alert) return null;
    const c = alert.coordinates;
    return `https://www.google.com/maps?q=${c.latitude},${c.longitude}`;
  }, [alert]);

  if (!session) return <Navigate to="/admin/agency/login" replace />;
  if (!alertId) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-rescue-bg p-4">
        <div className="max-w-md text-center" data-testid="agency-map-missing-alert">
          <AlertTriangle className="w-12 h-12 text-rose-600 mx-auto mb-3" />
          <h1 className="text-xl font-display font-black text-rescue-ink mb-2">Paramètre manquant</h1>
          <p className="text-rescue-mute mb-4">Cette page nécessite un <code>alert_id</code> dans l&apos;URL.</p>
          <button onClick={() => navigate("/admin/agency")} className="px-4 py-2 bg-rescue-ink text-white text-sm font-bold uppercase tracking-[0.15em]">Retour au tableau</button>
        </div>
      </div>
    );
  }

  // Full-screen denied / not-found shell.
  if (loadErr) {
    const Icon = loadErr.kind === "denied" ? AlertTriangle : MapPin;
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-rescue-bg p-4">
        <div className="max-w-md text-center" data-testid={`agency-map-error-${loadErr.kind}`}>
          <Icon className={`w-12 h-12 mx-auto mb-3 ${loadErr.kind === "denied" ? "text-rose-600" : "text-amber-600"}`} />
          <h1 className="text-xl font-display font-black text-rescue-ink mb-2">
            {loadErr.kind === "denied" ? "Accès refusé" : loadErr.kind === "notfound" ? "Alerte introuvable" : "Erreur"}
          </h1>
          <p className="text-rescue-mute mb-4">{loadErr.msg}</p>
          <button
            onClick={() => navigate("/admin/agency")}
            className="px-4 py-2 bg-rescue-ink text-white text-sm font-bold uppercase tracking-[0.15em]"
            data-testid="agency-map-back-btn"
          >
            Retour au tableau
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-[#0a0d12] text-zinc-100 flex flex-col" data-testid="agency-map-page">
      {/* Top bar */}
      <header className="flex items-center gap-2 px-3 pb-2.5 bg-[#0f1318] border-b border-zinc-800 z-10 pt-[calc(0.625rem+env(safe-area-inset-top))]">
        <button
          type="button"
          onClick={() => navigate("/admin/agency")}
          data-testid="agency-map-back"
          className="p-2 -ml-1 hover:bg-zinc-800 rounded"
          aria-label="Retour"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="text-[9px] font-bold uppercase tracking-[0.22em] text-emerald-400">
            Itinéraire {brand}
          </div>
          <div className="text-sm font-display font-black tracking-tight truncate">
            {loading ? "Chargement…" : (alert?.emergency_type || "Incident")}
            {alert?.operational_status && <span className="ml-2 text-zinc-400 font-normal text-[11px]">· {alert.operational_status}</span>}
          </div>
        </div>
        <button
          type="button"
          onClick={() => { agencyAuth.clear(); navigate("/admin/agency/login"); }}
          className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500 hover:text-zinc-100 px-2"
        >
          Déconnexion
        </button>
      </header>

      {/* Map container */}
      <div className="flex-1 relative">
        <div ref={containerRef} className="absolute inset-0" data-testid="agency-map-canvas" />

        {/* Loading veil */}
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-[#0a0d12]/80">
            <div className="flex items-center gap-2 text-emerald-300 text-sm">
              <Loader2 className="w-4 h-4 animate-spin" /> Chargement de la carte…
            </div>
          </div>
        )}

        {/* GPS-unavailable banner */}
        {!loading && !gpsCoords && alert && (
          <div
            data-testid="agency-map-gps-banner"
            className="absolute top-3 left-3 right-3 sm:right-auto sm:max-w-sm bg-amber-900/80 border border-amber-500/60 text-amber-100 text-[11px] px-3 py-2 rounded backdrop-blur-sm"
          >
            <strong className="block font-bold uppercase tracking-[0.15em] text-[10px] mb-0.5">Position actuelle indisponible</strong>
            L&apos;incident est affiché sur la carte. Autorisez la géolocalisation pour voir l&apos;itinéraire.
          </div>
        )}

        {/* Recenter controls */}
        {!loading && alert && (
          <div className="absolute right-3 flex flex-col gap-2" style={{ bottom: "calc(6rem + env(safe-area-inset-bottom))" }}>
            <button
              type="button"
              onClick={recenterOnIncident}
              data-testid="agency-map-recenter-incident"
              title="Centrer sur l'incident"
              className="p-2.5 bg-rose-600 hover:bg-rose-500 text-white rounded shadow-lg"
            >
              <Crosshair className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={recenterOnMe}
              data-testid="agency-map-recenter-me"
              title="Centrer sur ma position"
              className="p-2.5 bg-sky-600 hover:bg-sky-500 text-white rounded shadow-lg disabled:opacity-50"
              disabled={!gpsCoords}
            >
              <Locate className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Bottom action bar */}
        {!loading && alert && (
          <div className="absolute left-3 right-3 z-10" style={{ bottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}>
            <div className="bg-[#0f1318]/95 backdrop-blur-md border border-zinc-700 p-2.5 flex items-center gap-2">
              <button
                type="button"
                onClick={() => setRouteOpen(true)}
                data-testid="agency-map-show-route"
                className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-emerald-600 hover:bg-emerald-500 text-zinc-950 text-[11px] font-bold uppercase tracking-[0.12em]"
              >
                <Navigation2 className="w-3.5 h-3.5" />
                {routeLoading ? "Calcul…" : routeData ? `${(routeData.summary?.lengthMeters / 1000).toFixed(1)} km · ${Math.round((routeData.summary?.travelTimeSeconds || 0) / 60)} min` : "Itinéraire"}
              </button>
              {alert?.id && (
                <a
                  href={`/admin/agency#alert-${alert.id}`}
                  data-testid="agency-map-open-case-file"
                  className="inline-flex items-center gap-1.5 px-3 py-2 border border-zinc-600 text-zinc-100 hover:bg-zinc-800 text-[11px] font-bold uppercase tracking-[0.12em]"
                >
                  <Activity className="w-3.5 h-3.5" /> Dossier
                </a>
              )}
              {externalMapsUrl && (
                <a
                  href={externalMapsUrl}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="agency-map-external-fallback"
                  className="inline-flex items-center gap-1.5 px-2 py-2 border border-zinc-700 text-zinc-400 hover:text-zinc-100 hover:border-zinc-500 text-[10px] font-semibold uppercase tracking-[0.1em]"
                  title="Ouvrir dans une application externe"
                >
                  <Phone className="w-3 h-3 hidden" /> Externe
                </a>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Slide-up turn-by-turn drawer */}
      <RouteDrawer
        open={routeOpen}
        onOpenChange={setRouteOpen}
        loading={routeLoading}
        route={routeData}
        destination={alert ? {
          name: alert.emergency_type || "Incident",
          city: alert.operational_status || "",
          lat: alert.coordinates.latitude,
          lng: alert.coordinates.longitude,
        } : null}
        onClear={() => {
          const map = mapRef.current;
          if (map) clearRoute(map);
          setRouteData(null);
        }}
      />
    </div>
  );
}
